import {
  STATION_ERROR_DETAILS,
  StationErrorDetailKeys,
} from '@/constant/stationErrorDetails'

export interface DecodedStationError {
  code: number
  bit: number | null
  /** Code shown to the user, e.g. "30007" for code 3000 bit 7 (same as BE Diagnose). */
  displayCode: string
  keys: StationErrorDetailKeys
}

export interface StationErrorDetailText {
  title: string
  reason: string
  actions: string[]
}

/**
 * errDetail is a bitmask; the error is the highest set bit, except bits 6 + 7
 * together, which mean 67. Same rule as BE DiagnosticService.FindErrorBit.
 */
export const findErrorBit = (detail?: number | null): number | null => {
  if (detail == null || detail <= 0) return null

  const indices = detail
    .toString(2)
    .split('')
    .reverse()
    .flatMap((bit, index) => (bit === '1' ? [index] : []))

  if (indices.includes(6) && indices.includes(7)) return 67

  return Math.max(...indices)
}

/** Only this code carries an errDetail bitmask; other codes ignore it. */
export const DETAILED_ERROR_CODE = 3000

/**
 * Resolve a station error to its translation keys.
 *
 * Accepts the raw code + errDetail bitmask (from the io::on / io::off log), or
 * for 3000 the composite code returned by Diagnose (e.g. 30007). errDetail is
 * only decoded for 3000. Returns null when the code is not in the catalogue.
 */
export const decodeStationError = (
  code?: number | null,
  detail?: number | null,
): DecodedStationError | null => {
  if (code == null) return null

  let baseCode = code
  let bit: number | null = null
  const codeStr = String(code)

  if (code === DETAILED_ERROR_CODE) {
    bit = findErrorBit(detail)
  } else if (
    codeStr.length > 4 &&
    codeStr.startsWith(String(DETAILED_ERROR_CODE))
  ) {
    baseCode = DETAILED_ERROR_CODE
    bit = Number(codeStr.slice(4))
  }

  const entry = STATION_ERROR_DETAILS[baseCode]
  if (!entry) return null

  return {
    code: baseCode,
    bit,
    displayCode: bit != null ? `${baseCode}${bit}` : String(baseCode),
    keys: (bit != null && entry.bits?.[bit]) || entry.default,
  }
}

export const getStationErrorDetailText = (
  t: (key: string) => string,
  code?: number | null,
  detail?: number | null,
): StationErrorDetailText | null => {
  const decoded = decodeStationError(code, detail)
  if (!decoded) return null

  return {
    title: t(decoded.keys.titleKey),
    reason: t(decoded.keys.reasonKey),
    actions: decoded.keys.actionKeys.map((key) => t(key)),
  }
}
